import React, { useEffect, useRef, useState } from "react";
import ChevronDown from "../../../../svg/chevron-down";

export interface PillSelectOption {
  label: string;
  value: string;
}

export interface PillSelectCellValue {
  /** Currently selected option value. */
  value: string;
  options: PillSelectOption[];
  /** Optional: tailwind class string per option value (background/text/border). */
  styleMap?: Record<string, string>;
  /** Class applied when value has no entry in styleMap. */
  fallbackStyle?: string;
  ariaLabel?: string;
  disabled?: boolean;
}

interface PillSelectCellProps {
  value: PillSelectCellValue;
  rowData?: any;
  header?: string;
  onCellValueChange?: (rowData: any, fieldKey: string, newValue: any) => void;
}

const PillSelectCell: React.FC<PillSelectCellProps> = ({
  value,
  rowData,
  header,
  onCellValueChange,
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node))
        setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const currentLabel =
    value.options.find((o) => o.value === value.value)?.label ||
    value.value ||
    "—";
  const currentStyle =
    value.styleMap?.[value.value] ||
    value.fallbackStyle ||
    "bg-primaryGray-16 text-primaryGray-7 border-primaryGray-13";

  const handleSelect = (next: string) => {
    setOpen(false);
    if (next !== value.value && onCellValueChange) {
      onCellValueChange(rowData, header || "", next);
    }
  };

  return (
    <div ref={ref} className="relative inline-block">
      <button
        type="button"
        aria-label={value.ariaLabel || `Select ${header || "value"}`}
        disabled={value.disabled}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((p) => !p);
        }}
        className={`flex items-center gap-1 rounded-md border px-2 py-1 text-x-tiny font-semibold ${currentStyle} ${
          value.disabled ? "cursor-not-allowed opacity-60" : ""
        }`}
      >
        <span>{currentLabel}</span>
        <ChevronDown
          rotate={open ? 180 : 0}
          stroke="currentColor"
          width={10}
          height={10}
        />
      </button>
      {open && (
        <div
          className="absolute z-30 mt-1 min-w-[7rem] rounded-md border border-primaryGray-14 bg-white shadow-md"
          onClick={(e) => e.stopPropagation()}
        >
          {value.options.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => handleSelect(opt.value)}
              className={`flex w-full items-center px-3 py-1.5 text-left text-tiny hover:bg-primaryGray-16 ${
                opt.value === value.value
                  ? "font-semibold text-primaryGray-1"
                  : "text-primaryGray-4"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default PillSelectCell;
